package dev.lingvo.site.pages.app.projects.slug.screenshots

import dev.lingvo.shared.Role
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.api.Session
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.runProjectAction
import kotlinx.coroutines.CancellationException

/** Separate claims prevent translation actions from inheriting a structure-write grant. */
internal enum class ScreenshotsPrivilege { PLAN, CAPTURE, CAPTION, DESIGN, COMPOSE, APPROVE, PUSH, SNAPSHOT, COMMENT }

/** Mirrors the server's privilege boundary while the server remains authoritative. */
internal fun screenshotsPrivilegeAllowed(privilege: ScreenshotsPrivilege, role: Role?, personSession: Boolean): Boolean =
    when (privilege) {
        ScreenshotsPrivilege.SNAPSHOT -> role == Role.OWNER
        ScreenshotsPrivilege.PLAN -> role == Role.OWNER || role == Role.ADMIN
        ScreenshotsPrivilege.APPROVE, ScreenshotsPrivilege.PUSH -> personSession && (role == Role.OWNER || role == Role.ADMIN)
        ScreenshotsPrivilege.COMMENT -> personSession && role != null && role != Role.VIEWER
        ScreenshotsPrivilege.CAPTURE, ScreenshotsPrivilege.CAPTION, ScreenshotsPrivilege.DESIGN, ScreenshotsPrivilege.COMPOSE ->
            role != null && role != Role.VIEWER
    }

/** Retires results when the exact route, membership or session that authorized an action changes. */
internal class ScreenshotsActionRunner(
    slug: String,
    private val launch: (suspend () -> Unit) -> Unit,
    private val currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(slug) },
    private val memberships: () -> List<WorkspaceDto> = { Session.organizations },
    private val personSession: () -> Boolean = { Session.isAuthenticated && Session.token?.split('.')?.size == 3 },
) {
    private val claims = ScreenshotsPrivilege.entries.associateWith { privilege ->
        val route = currentRoute()
        val authority = ProjectAuthority.resolve(route?.project, memberships())
        if (screenshotsPrivilegeAllowed(privilege, authority.role, personSession())) {
            ProjectActionClaim.capture(route, memberships(), when (privilege) {
                ScreenshotsPrivilege.PLAN, ScreenshotsPrivilege.APPROVE, ScreenshotsPrivilege.PUSH, ScreenshotsPrivilege.SNAPSHOT -> ProjectPrivilege.STRUCTURE
                else -> ProjectPrivilege.READ
            })
        } else null
    }

    /** Checks at dispatch and coroutine entry, then retires success, errors and busy updates after authority changes. */
    fun <T> run(
        privilege: ScreenshotsPrivilege, setBusy: (Boolean) -> Unit, transport: suspend () -> T,
        onFailure: (Throwable) -> Unit, onSuccess: (T) -> Unit,
    ) {
        val claim = claims[privilege]?.takeIf { authorized(privilege, it) } ?: return
        launch {
            if (!authorized(privilege, claim)) return@launch
            runProjectAction(claim, currentRoute, memberships,
                setBusy = { if (authorized(privilege, claim)) setBusy(it) },
                transport = {
                    if (!authorized(privilege, claim)) throw CancellationException("Screenshot authority changed")
                    transport()
                },
                onSuccess = { if (authorized(privilege, claim)) onSuccess(it) },
                onFailure = { if (authorized(privilege, claim)) onFailure(it) },
            )
        }
    }

    private fun authorized(privilege: ScreenshotsPrivilege, claim: ProjectActionClaim): Boolean {
        val route = currentRoute()
        val currentMemberships = memberships()
        return claim.remainsAuthorized(route, currentMemberships) && screenshotsPrivilegeAllowed(
            privilege, ProjectAuthority.resolve(route?.project, currentMemberships).role, personSession(),
        )
    }
}
